import { motion } from 'framer-motion';
import { Building2, Crown, AlertCircle } from 'lucide-react';
import { SceneFrame, Pill } from './_atoms';

function Entity({ label, delay, color = 'bg-teal-600' }) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 10, scale: 0.85 }}
      animate={{ opacity: 1, y: 0, scale: 1 }}
      transition={{ delay, duration: 0.4 }}
      className={`rounded-lg ${color} text-white px-3 py-2 text-[11.5px] font-body font-semibold text-center shadow-sm`}
    >{label}</motion.div>
  );
}

export default function ArchStructureScene() {
  return (
    <SceneFrame
      eyebrow="Structural hierarchy"
      title="Two ways a entity can live"
      subtitle="Both models converge at the Entity Category layer — so access rules and role catalogs apply consistently regardless of ownership."
    >
      <div className="grid grid-cols-2 gap-8 mt-4">

        {/* ========= Organization Model ========= */}
        <motion.div
          initial={{ opacity: 0, x: -20 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: 0.3 }}
          className="rounded-2xl bg-white border border-[#E5E7EB] p-6 shadow-sm"
        >
          <div className="flex items-center gap-2.5 mb-1">
            <Crown size={18} className="text-amber-500" />
            <h3 className="font-heading font-extrabold text-[17px] text-[#0A0A0A]">Organization Model</h3>
          </div>
          <p className="font-body text-[12px] text-[#6B7280] mb-5">A parent entity that owns and governs one or more entities.</p>

          <div className="flex flex-col items-center gap-3">
            {/* Parent org */}
            <motion.div
              initial={{ scale: 0 }} animate={{ scale: 1 }} transition={{ delay: 0.5, type: 'spring', stiffness: 260 }}
              className="rounded-xl bg-gradient-to-br from-amber-500 to-amber-600 text-white px-5 py-3 font-body font-bold text-[13px] shadow-md"
            >Nextcare (Payer)</motion.div>
            <motion.div
              initial={{ opacity: 0, scaleY: 0 }} animate={{ opacity: 1, scaleY: 1 }} transition={{ delay: 0.8 }}
              className="w-[2px] h-4 bg-[#D1D5DB] origin-top"
            />
            <div className="grid grid-cols-3 gap-2 w-full">
              <Entity label="NC — AUH" delay={1.0} color="bg-amber-500" />
              <Entity label="NC — DXB" delay={1.15} color="bg-amber-500" />
              <Entity label="NC — SHJ" delay={1.3} color="bg-amber-500" />
            </div>
          </div>

          <div className="mt-5 rounded-xl bg-amber-50 border border-amber-200 px-3.5 py-2.5 flex items-start gap-2">
            <AlertCircle size={13} className="text-amber-600 flex-shrink-0 mt-0.5" />
            <p className="text-[11.5px] font-body text-amber-900 leading-snug">
              <span className="font-bold">Same-category rule —</span> all entities under one organization must share the same category.
            </p>
          </div>
        </motion.div>

        {/* ========= Standalone Model ========= */}
        <motion.div
          initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: 0.5 }}
          className="rounded-2xl bg-white border border-[#E5E7EB] p-6 shadow-sm"
        >
          <div className="flex items-center gap-2.5 mb-1">
            <Building2 size={18} className="text-teal-600" />
            <h3 className="font-heading font-extrabold text-[17px] text-[#0A0A0A]">Standalone Entity Model</h3>
          </div>
          <p className="font-body text-[12px] text-[#6B7280] mb-5">A entity that operates independently — no parent organization.</p>

          <div className="grid grid-cols-3 gap-2 mt-9">
            <Entity label="Rashid Hospital"  delay={1.0} color="bg-teal-600" />
            <Entity label="Zulekha Clinic"   delay={1.15} color="bg-teal-600" />
            <Entity label="Aster Pharmacy"   delay={1.3} color="bg-teal-600" />
          </div>

          <div className="mt-5 rounded-xl bg-teal-50 border border-teal-200 px-3.5 py-2.5 flex items-start gap-2">
            <AlertCircle size={13} className="text-teal-600 flex-shrink-0 mt-0.5" />
            <p className="text-[11.5px] font-body text-teal-900 leading-snug">
              <span className="font-bold">Cross-standalone rule —</span> if a user spans multiple standalone entities, they must all share the same category.
            </p>
          </div>
        </motion.div>
      </div>

      <motion.div
        initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 1.8 }}
        className="mt-6 flex items-center gap-2 justify-center"
      >
        <Pill tone="slate">Why this matters</Pill>
        <p className="text-[12px] font-body text-[#6B7280]">
          Mixing categories inside a single scope would create module conflicts and unintended cross-entity visibility.
        </p>
      </motion.div>
    </SceneFrame>
  );
}
